import { LoadingState, QueryError } from '#/components/common/loading-state'
/**
 * `/templates/:slug` — one published template: what it contains and a button that turns it into
 * a real plan (`POST /api/plan/new/template`). The PRD body never travels; only the outline does.
 */
import { useState } from 'react'
import { ArrowLeft, Loader2 } from 'lucide-react'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { Container, MkFooter, MkLink, MkNav, Overline } from '#/components/landing/mk'
import { ApiError } from '#/lib/api'
import { usePublicTemplate, useTemplateInstance } from '#/lib/api-flywheel'
import { useHydratedSession } from '#/lib/auth-client'
import { m } from '#/paraglide/messages'

export const Route = createFileRoute('/templates/$slug')({
  component: Page,
})


function Page() {
  const { slug } = Route.useParams()
  const navigate = useNavigate()
  const { data, isLoading, isError, refetch, isFetching } = usePublicTemplate(slug)
  const { data: session } = useHydratedSession()
  const use = useTemplateInstance()
  const [error, setError] = useState<string | null>(null)
  const t = data?.template ?? null
  const signedIn = Boolean(session?.user)

  const start = async () => {
    setError(null)
    try {
      const r = await use.mutateAsync({ slug })
      navigate({ to: '/workspace/$workspaceId/$view', params: { workspaceId: r.workspaceId, view: 'roadmap' }, search: { plan: r.planId } })
    } catch (e) {
      setError(e instanceof ApiError ? e.message : m.common_error_generic())
    }
  }

  return (
    <div className="mk h-full overflow-y-auto overflow-x-hidden scroll-smooth">
      <MkNav />
      <main>
        <section className="pb-10 pt-12 lg:pt-16">
          <Container className="flex flex-col gap-6">
            <Link to="/templates" search={{ tag: undefined }} className="inline-flex w-fit items-center gap-1.5 text-[15px] text-[var(--mk-fg-muted)] hover:text-[var(--mk-fg)]">
              <ArrowLeft className="size-4" aria-hidden /> {m.fw_tpl_back()}
            </Link>
            {isError ? <QueryError onRetry={refetch} retrying={isFetching} /> : isLoading ? (
              <LoadingState compact label={m.common_loading()} />
            ) : !t ? (
              <div className="mk-card flex flex-col gap-2 p-8">
                <p className="mk-muted">{m.fw_tpl_notfound()}</p>
              </div>
            ) : (
              <>
                <div className="flex flex-col gap-4">
                  <Overline>{m.fw_tpl_overline()}</Overline>
                  <h1 className="max-w-[24ch]">
                    {t.title}{' '}
                    <span className="align-middle rounded border border-[var(--mk-border)] px-2 py-0.5 text-[15px] font-medium uppercase tracking-wide text-[var(--mk-fg-muted)]">{t.locale}</span>
                  </h1>
                  <p className="mk-lead max-w-[62ch]">{t.summary}</p>
                  {t.tags.length ? (
                    <ul className="flex flex-wrap gap-1.5">
                      {t.tags.map((tag) => (
                        <li key={tag} className="rounded-full border border-[var(--mk-border)] px-2.5 py-0.5 text-[13px] text-[var(--mk-fg-muted)]">
                          {tag}
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </div>

                <dl className="grid gap-4 sm:grid-cols-4">
                  {[
                    { k: m.fw_tpl_pass(), v: t.passRate === null ? '—' : `${Math.round(t.passRate * 100)}%` },
                    { k: m.fw_bm_col_tasks(), v: String(t.tasks) },
                    { k: m.fw_tpl_verified_short(), v: String(t.verifiedTasks) },
                    { k: m.fw_tpl_uses_short(), v: String(t.uses) },
                  ].map((s) => (
                    <div key={s.k} className="mk-card-white flex flex-col gap-1 p-5">
                      <dd className="text-3xl font-semibold tabular-nums text-[var(--mk-fg)]">{s.v}</dd>
                      <dt className="text-[13px] uppercase tracking-wide text-[var(--mk-fg-muted)]">{s.k}</dt>
                    </div>
                  ))}
                </dl>

                <div className="flex flex-col gap-3">
                  <div className="flex flex-wrap items-center gap-3">
                    {signedIn ? (
                      <button type="button" onClick={start} disabled={use.isPending} className="mk-btn mk-btn-primary h-12">
                        {use.isPending ? <Loader2 className="size-4 animate-spin" aria-hidden /> : null}
                        {use.isPending ? m.fw_tpl_using() : m.fw_tpl_use()}
                      </button>
                    ) : (
                      <MkLink to="/sign-in" search={{ redirect: `/templates/${slug}` }} className="h-12">
                        {m.fw_tpl_signin()}
                      </MkLink>
                    )}
                    <span className="mk-muted max-w-[44ch] text-[14px]">{m.fw_tpl_use_hint()}</span>
                  </div>
                  {error ? <p className="text-[15px] text-red-600">{error}</p> : null}
                </div>

                <div className="flex flex-col gap-4">
                  <h2>{m.fw_tpl_outline()}</h2>
                  <p className="mk-muted max-w-[62ch]">{m.fw_tpl_no_prd()}</p>
                  <ol className="flex flex-col gap-3">
                    {t.features.map((f, i) => (
                      <li key={`${f.name}-${i}`} className="mk-card-white flex flex-col gap-2 p-5">
                        <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
                          <h3 className="min-w-0">{f.name}</h3>
                          <span className="text-[13px] tabular-nums text-[var(--mk-fg-muted)]">
                            {m.fw_tpl_phase({ n: f.phase })} · {m.fw_tpl_tasks({ n: f.tasks })}
                          </span>
                        </div>
                        {f.summary ? <p className="mk-muted leading-relaxed">{f.summary}</p> : null}
                        {f.subs.length ? (
                          <ul className="flex flex-wrap gap-1.5">
                            {f.subs.map((s) => (
                              <li key={s} className="rounded-full bg-[var(--mk-bg-muted)] px-2.5 py-0.5 text-[13px] text-[var(--mk-fg-muted)]">
                                {s}
                              </li>
                            ))}
                          </ul>
                        ) : null}
                      </li>
                    ))}
                  </ol>
                </div>
              </>
            )}
          </Container>
        </section>
      </main>
      <MkFooter />
    </div>
  )
}
